import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { AxiosError } from 'axios';
import landscapeDocumentService from '../services/landscape-document.service';
import { LandscapeDocumentResponseDto } from '../dto/response/landscape-document-response.dto';
import { LandscapeDocumentUpdateRequestDto } from '../dto/request/landscape-document-update-request.dto';
import { LocalesLanguagesConstants } from '../../../../locales/locales';
import { LandscapeDocumentSaveRequestDto } from '../dto/request/landscape-document-save-request.dto';

const initialState = {
  status: 'idle',
  landscapeDocuments: [] as LandscapeDocumentResponseDto[] | null,
  landscapeDocument: {} as LandscapeDocumentResponseDto | null,
  error: null as string | null,
};

export const fetchLandscapeDocumentById = createAsyncThunk(
  'landscapeDocuments/fetchLandscapeDocumentById',
  async (payload: { id: string; language: LocalesLanguagesConstants }) => {
    const { id, language } = payload;
    return await landscapeDocumentService.fetchLandscapeDocumentById(
      id,
      language
    );
  }
);

export const fetchLandscapeDocumentsByProjectId = createAsyncThunk(
  'landscapeDocuments/fetchLandscapeDocumentsByProjectId',
  async (projectId: string) => {
    return await landscapeDocumentService.fetchLandscapeDocumentsByProjectId(
      projectId
    );
  }
);

export const fetchLandscapeDocumentsByLandscapeId = createAsyncThunk(
  'landscapeDocuments/fetchLandscapeDocumentsByLandscapeId',
  async (landscapeId: string) => {
    return await landscapeDocumentService.fetchLandscapeDocumentsByLandscapeId(
      landscapeId
    );
  }
);

export const fetchLandscapeDocumentsByLandscapeIndicatorId = createAsyncThunk(
  'landscapeDocuments/fetchLandscapeDocumentsByLandscapeIndicatorId',
  async (landscapeIndicatorId: string) => {
    return await landscapeDocumentService.fetchLandscapeDocumentsByLandscapeIndicatorId(
      landscapeIndicatorId
    );
  }
);

export const saveLandscapeDocument = createAsyncThunk(
  'landscapeDocuments/saveLandscapeDocument',
  async (saveRequest: LandscapeDocumentSaveRequestDto, { rejectWithValue }) => {
    try {
      return await landscapeDocumentService.saveLandscapeDocument(saveRequest);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

export const updateLandscapeDocument = createAsyncThunk(
  'landscapeDocuments/updateLandscapeDocument',
  async (
    payload: {
      document: LandscapeDocumentUpdateRequestDto;
      language: LocalesLanguagesConstants;
    },
    { rejectWithValue }
  ) => {
    const { document, language } = payload;
    try {
      return await landscapeDocumentService.updateLandscapeDocument(
        document,
        language
      );
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

export const deleteLandscapeDocument = createAsyncThunk(
  'landscapeDocuments/deleteLandscapeDocument',
  async (
    params: { existingId: string; compactId: string | null },
    { rejectWithValue }
  ) => {
    const { existingId, compactId } = params;
    try {
      return await landscapeDocumentService.deleteLandscapeDocument(
        existingId,
        compactId
      );
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const landscapeDocumentSlice = createSlice({
  name: 'landscapeDocumentSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLandscapeDocumentById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchLandscapeDocumentById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.landscapeDocument = action.payload;
      })
      .addCase(fetchLandscapeDocumentById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchLandscapeDocumentsByProjectId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        fetchLandscapeDocumentsByProjectId.fulfilled,
        (state, action) => {
          state.status = 'succeeded';
          state.landscapeDocuments = action.payload;
        }
      )
      .addCase(fetchLandscapeDocumentsByProjectId.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(fetchLandscapeDocumentsByLandscapeId.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        fetchLandscapeDocumentsByLandscapeId.fulfilled,
        (state, action) => {
          state.status = 'succeeded';
          state.landscapeDocuments = action.payload;
        }
      )
      .addCase(
        fetchLandscapeDocumentsByLandscapeId.rejected,
        (state, action) => {
          state.status = 'failed';
          state.error = action.error?.message ?? null;
        }
      )
      .addCase(
        fetchLandscapeDocumentsByLandscapeIndicatorId.pending,
        (state) => {
          state.status = 'loading';
        }
      )
      .addCase(
        fetchLandscapeDocumentsByLandscapeIndicatorId.fulfilled,
        (state, action) => {
          state.status = 'succeeded';
          state.landscapeDocuments = action.payload;
        }
      )
      .addCase(
        fetchLandscapeDocumentsByLandscapeIndicatorId.rejected,
        (state, action) => {
          state.status = 'failed';
          state.error = action.error?.message ?? null;
        }
      )
      .addCase(saveLandscapeDocument.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveLandscapeDocument.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.landscapeDocuments?.push(action.payload);
        showSuccess('Document added successfully.');
      })
      .addCase(saveLandscapeDocument.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Adding document failed. ${action.payload as string}`);
      })
      .addCase(updateLandscapeDocument.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateLandscapeDocument.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(updateLandscapeDocument.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Saving document failed. ${action.payload as string}`);
      })
      .addCase(deleteLandscapeDocument.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteLandscapeDocument.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Document deleted successfully.');
      })
      .addCase(deleteLandscapeDocument.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting document failed. ${action.payload as string}`);
      });
  },
});

export default landscapeDocumentSlice.reducer;
